import type { Theme } from '@mui/material/styles';

import type {
  StripeElementLocale,
  StripeElementsOptionsClientSecret,
} from '@stripe/stripe-js';

import { ThemeMode } from '../../shared/constants';

function mapAppLanguageToStripeLocale(
  i18nLanguage: string | undefined,
): StripeElementLocale {
  const base = i18nLanguage?.split('-')[0]?.toLowerCase() ?? 'en';
  const map: Record<string, StripeElementLocale> = {
    en: 'en',
    ru: 'ru',
    pl: 'pl',
    // Stripe public locales list has no `uk`; avoid invalid values.
    ua: 'en',
    by: 'ru',
  };
  return map[base] ?? 'auto';
}

/**
 * Maps MUI theme to Stripe Elements appearance so Payment Element matches app light/dark.
 */
export function buildStripeElementsOptions(params: {
  clientSecret: string;
  muiTheme: Theme;
  themeMode: ThemeMode;
  i18nLanguage: string | undefined;
}): StripeElementsOptionsClientSecret {
  const { clientSecret, muiTheme, themeMode, i18nLanguage } = params;
  const { palette, shape, typography } = muiTheme;

  return {
    clientSecret,
    locale: mapAppLanguageToStripeLocale(i18nLanguage),
    loader: 'auto',
    appearance: {
      theme: themeMode === ThemeMode.dark ? 'night' : 'stripe',
      variables: {
        colorPrimary: palette.primary.main,
        colorBackground: palette.background.paper,
        colorText: palette.text.primary,
        colorDanger: palette.error.main,
        colorTextSecondary: palette.text.secondary,
        colorTextPlaceholder: palette.text.secondary,
        borderRadius: `${shape.borderRadius}px`,
        fontFamily: typography.fontFamily as string,
        spacingUnit: '4px',
      },
      rules: {
        '.Tab': {
          borderColor: muiTheme.palette.divider,
        },
        '.Input': {
          borderColor: muiTheme.palette.divider,
        },
      },
    },
  };
}
